import { css } from "@emotion/react";
import { useForm } from "@tanstack/react-form";
import { motion } from "framer-motion";
import { ChevronLeftIcon, CircleAlert, LogIn, UserPlus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import type { CheckoutFormState } from "../../hooks/useCheckoutFormState";
import { useSessionState } from "../../hooks/useSessionState";
import { useShoppingCartItemsState } from "../../hooks/useShoppingCartItemsState";
import { useStorefrontSettingsState } from "../../hooks/useStorefrontSettingsState";
import { useTranslate } from "../../hooks/useTranslate/useTranslate";
import type { StorefrontProgress } from "../ProgressMeter";
import { ProgressMeter } from "../ProgressMeter";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { ReservationDates } from "./ReservationDates";
import { SelectDeal } from "./SelectDeal";
import { SelectLocation } from "./SelectLocation";

export type CheckoutFormProps = {
	checkoutFormState: CheckoutFormState;
};
export const CheckoutForm: React.FC<CheckoutFormProps> = (props) => {
	const translate = useTranslate();
	const sessionState = useSessionState();
	const fieldSpacing: string = "1em";
	const isFormDisabled = props.checkoutFormState.isFormDisabled();
	const navigate = useNavigate();
	const location = useLocation();
	const redirectTarget = `${location.pathname}${location.search}`;

	const shoppingCartItemsState = useShoppingCartItemsState();

	const quoteDescription = props.checkoutFormState.get().quoteDescription ?? "";
	const jobName = props.checkoutFormState.get().jobName ?? "";
	const selectedDealId = props.checkoutFormState.get().dealIdValue ?? "";
	const [isQuoteDescriptionFocused, setIsQuoteDescriptionFocused] =
		useState(false);
	const [isJobNameFocused, setIsJobNameFocused] = useState(false);

	const QUOTEDESCRIPTION_LENGTH = 50;
	const JOBNAME_LENGTH = 255;

	const progressMeterSessionId =
		props.checkoutFormState.get().progressMeterSessionId;
	const progressMeterOpen = props.checkoutFormState.get().progressMeterOpen;

	const storefrontSettingsState = useStorefrontSettingsState();
	const isUniversity =
		storefrontSettingsState.get().settings?.IsUniversity ?? false;
	const separateQuoteRequestsByCatalog =
		storefrontSettingsState.get().settings?.SeparateQuoteRequestsByCatalog ??
		false;
	const makeQuoteRequestIntoOrderOnSubmission =
		storefrontSettingsState.get().settings
			?.MakeQuoteRequestIntoOrderOnSubmission ?? false;
	const registerEnabled =
		storefrontSettingsState.get().settings?.RegisterEnabled ?? false;

	let { catalogid } = useParams();
	if (!separateQuoteRequestsByCatalog) {
		catalogid = undefined;
	}
	let pageHeadingText = "";
	if (isUniversity) {
		pageHeadingText = translate("Submit Equipment Request");
	} else {
		if (!makeQuoteRequestIntoOrderOnSubmission) {
			pageHeadingText = translate("Submit Request for Quote");
		} else {
			pageHeadingText = translate("Submit Order");
		}
	}
	let captionDescription = "";
	if (isUniversity) {
		captionDescription = translate("Order Description");
	} else {
		if (!makeQuoteRequestIntoOrderOnSubmission) {
			captionDescription = translate("Quote Description");
		} else {
			captionDescription = translate("Order Description");
		}
	}

	const onInit = useCallback(() => {
		(async () => {
			if (
				!props.checkoutFormState.get().deals &&
				!props.checkoutFormState.get().isLoadingDeals &&
				!props.checkoutFormState.get().isLoadingDealsFailed
			) {
				props.checkoutFormState.loadDealsAsync();
			}
		})();
	}, [props.checkoutFormState]);

	useEffect(() => {
		onInit();
	}, [onInit]);
	const checkoutForm = useForm({
		defaultValues: {
			quoteDescription: props.checkoutFormState.get().quoteDescription ?? "",
			jobName: props.checkoutFormState.get().jobName ?? "",
			notes: props.checkoutFormState.get().notesValue ?? "",
		},
		onSubmit: async ({ value }) => {
			props.checkoutFormState.setQuoteDescription(value.quoteDescription);
			props.checkoutFormState.setJobName(value.jobName);
			props.checkoutFormState.setNotes(value.notes);
			props.checkoutFormState.validateForm();
			if (
				!props.checkoutFormState.get().fromDateValue ||
				!props.checkoutFormState.get().toDateValue
			) {
				return;
			}
			await props.checkoutFormState.submitQuoteRequestAsync(catalogid);
		},
	});
	const getCheckoutFieldError = (errors: readonly unknown[]) => {
		return typeof errors[0] === "string" ? errors[0] : "";
	};

	return (
		<div
			className="w-full sm:max-w-120"
			css={css`
            padding: 1em 1em;
            margin: 0 auto;
            .field {
                flex: 1 1 0;
            }
            .field .caption {
                text-align: center;
            }
            .react-datepicker__input-container > input {
                width: 100%;
                box-sizing: border-box;
            }
	            `}
		>
			<Card className="px-1">
				<CardHeader>
					<CardTitle>{pageHeadingText}</CardTitle>
					<CardDescription>
						{translate(
							"Please fill out any remaining fields and make sure the reservation dates are correct.",
						)}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div>
						{props.checkoutFormState.get().hasSubmitError && (
							<Alert variant="destructive">
								<CircleAlert className="size-4" />
								<AlertDescription>
									{props.checkoutFormState.get().submitError ?? ""}
								</AlertDescription>
							</Alert>
						)}
						<div css={css`margin:1em 0 0 0;`}>
							<checkoutForm.Field
								name="quoteDescription"
								validators={{
									onSubmit: ({ value }) =>
										value.length > QUOTEDESCRIPTION_LENGTH
											? `${captionDescription} must be ${QUOTEDESCRIPTION_LENGTH} characters or less`
											: undefined,
								}}
							>
								{(field) => {
									const showError =
										field.state.meta.isTouched ||
										checkoutForm.state.submissionAttempts > 0;
									const error = getCheckoutFieldError(
										field.state.meta.errors,
									);
									return (
										<Field>
											<FieldLabel
												htmlFor="txtQuoteDescription"
												className="mb-1"
											>
												{captionDescription}
												{isQuoteDescriptionFocused
													? ` (${quoteDescription.length} of ${QUOTEDESCRIPTION_LENGTH} chars)`
													: ""}
											</FieldLabel>
											{showError && error.length > 0 && (
												<FieldError>{error}</FieldError>
											)}
											<Textarea
												id="txtQuoteDescription"
												autoComplete="quotedescription"
												disabled={isFormDisabled}
												maxLength={QUOTEDESCRIPTION_LENGTH}
												value={field.state.value}
												className="dark:bg-input/0 border-input focus-visible:border-ring focus-visible:ring-ring/50 min-h-[80px] w-full rounded-lg border bg-transparent px-2.5 py-2 text-sm outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
												onChange={(e) => {
													const value = e.currentTarget.value;
													field.handleChange(value);
													props.checkoutFormState.setQuoteDescription(value);
												}}
												onFocus={(_e) => {
													setIsQuoteDescriptionFocused(true);
												}}
												onBlur={() => {
													setIsQuoteDescriptionFocused(false);
													field.handleBlur();
												}}
												aria-invalid={showError && error.length > 0}
											/>
										</Field>
									);
								}}
							</checkoutForm.Field>
						</div>
						{!isUniversity && (
							<>
								{props.checkoutFormState.get().deals &&
									(props.checkoutFormState.get().deals?.length ?? 0) > 0 && (
										<div css={css`margin:1em 0 0 0;`}>
											<SelectDeal disabled={isFormDisabled} />
										</div>
									)}
								{(!props.checkoutFormState.get().deals ||
									(props.checkoutFormState.get().deals?.length ?? 0) === 0 ||
									selectedDealId === "-") && (
									<div css={css`margin:1em 0 0 0;`}>
										<div css={css`margin:1em 0 0 0;`}>
											<checkoutForm.Field
												name="jobName"
												validators={{
													onSubmit: ({ value }) =>
														value.length > JOBNAME_LENGTH
															? `${translate("Job")} must be ${JOBNAME_LENGTH} characters or less`
															: undefined,
												}}
											>
												{(field) => {
													const showError =
														field.state.meta.isTouched ||
														checkoutForm.state.submissionAttempts > 0;
													const error = getCheckoutFieldError(
														field.state.meta.errors,
													);
													return (
														<Field>
															<FieldLabel htmlFor="txtJobName" className="mb-1">
																{translate("Job")}
																{isJobNameFocused
																	? ` (${jobName.length} of ${JOBNAME_LENGTH} chars)`
																	: ""}
															</FieldLabel>
															{showError && error.length > 0 && (
																<FieldError>{error}</FieldError>
															)}
															<Input
																id="txtJobName"
																type="text"
																autoComplete="jobname"
																disabled={isFormDisabled}
																maxLength={JOBNAME_LENGTH}
																value={field.state.value}
																onChange={(e) => {
																	const value = e.currentTarget.value;
																	field.handleChange(value);
																	props.checkoutFormState.setJobName(value);
																}}
																onFocus={(_e) => {
																	setIsJobNameFocused(true);
																}}
																onBlur={() => {
																	setIsJobNameFocused(false);
																	field.handleBlur();
																}}
																aria-invalid={showError && error.length > 0}
															/>
														</Field>
													);
												}}
											</checkoutForm.Field>
										</div>
									</div>
								)}
								<div css={css`margin:1em 0 0 0;`}>
									<SelectLocation disabled={isFormDisabled} required={true} />
								</div>
							</>
						)}

						{shoppingCartItemsState.hasCatalogTypes().hasRental && (
							<div className="flex items-center justify-center mt-1">
								<ReservationDates
									fullwidth={true}
									catalogId={catalogid}
									required={true}
								/>
							</div>
						)}
					</div>

					<div
						css={css`
			                margin:${fieldSpacing} 0;`}
					>
						<checkoutForm.Field name="notes">
							{(field) => {
								const showError =
									field.state.meta.isTouched ||
									checkoutForm.state.submissionAttempts > 0;
								const error = getCheckoutFieldError(field.state.meta.errors);
								return (
									<Field>
										<FieldLabel htmlFor="checkout-notes" className="mb-1">
											{translate("Notes")}
										</FieldLabel>
										{showError && error.length > 0 && (
											<FieldError>{error}</FieldError>
										)}
										<Textarea
											id="checkout-notes"
											disabled={isFormDisabled}
											className="dark:bg-input/0 border-input focus-visible:border-ring focus-visible:ring-ring/50 min-h-[150px] w-full rounded-lg border bg-transparent px-2.5 py-2 text-sm outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
											value={field.state.value}
											onChange={(e) => {
												const value = e.currentTarget.value;
												field.handleChange(value);
												props.checkoutFormState.setNotes(value);
											}}
											onBlur={field.handleBlur}
											aria-invalid={showError && error.length > 0}
										/>
									</Field>
								);
							}}
						</checkoutForm.Field>
					</div>
					<Button
						type="button"
						variant="outline"
						className="w-full mb-2"
						onClick={() => navigate(-1)}
					>
						<ChevronLeftIcon />
						{translate("Back")}
					</Button>
					{!sessionState.get().loggedIn && (
						<div
							css={css`flex:1 1 0;position:relative;display:flex;align-items:center;justify-content:center;`}
						>
							<div
								css={css`
										flex: 1 1 0;
										display:flex;
										margin-top:4px;
										margin-bottom:4px;
										align-items:center;
										justify-content:center;`}
							>
								<Button
									type="button"
									className="flex-1"
									disabled={isFormDisabled}
									variant="outline"
									onClick={async (_e) => {
										return Promise.resolve().then(() => {
											navigate(
												`/login?redirect=${encodeURIComponent(redirectTarget)}`,
											);
										});
									}}
								>
									<motion.div
										layoutId="loginform"
										transition={{ type: "spring", stiffness: 300, damping: 22 }}
										css={css`
												display:flex;
												align-items:center;`}
									>
										{translate("Login")}
										<LogIn className="ml-1 size-4" />
									</motion.div>
								</Button>
								{registerEnabled && (
									<Button
										type="button"
										disabled={isFormDisabled}
										className="ml-4 flex-1"
										variant="outline"
										onClick={async (_e) => {
											return Promise.resolve().then(() => {
												navigate(
													`/register?redirect=${encodeURIComponent(redirectTarget)}`,
												);
											});
										}}
									>
										<motion.div
											layoutId="registerform"
											transition={{
												type: "spring",
												stiffness: 300,
												damping: 22,
											}}
											css={css`
													display:flex;
													align-items:center;`}
										>
											{translate("Register")}
											<UserPlus className="ml-1 size-4" />
										</motion.div>
									</Button>
								)}
							</div>
						</div>
					)}
					<div
						css={css`
			                    display:flex;
			                    margin-top:16px;
			                    margin-bottom:${fieldSpacing};
			                    align-items:center;
			                    justify-content:center;`}
					>
						<Button
							type="button"
							className="w-full"
							disabled={
								isFormDisabled ||
								!sessionState.get().loggedIn ||
								checkoutForm.state.isSubmitting
							}
							variant="default"
							onClick={() => {
								void checkoutForm.handleSubmit();
							}}
						>
							{translate("Submit")}
							{/*<Done css={css`margin: 0 0 0 .5em;color:#00bcd4;`} />*/}
						</Button>
					</div>
				</CardContent>
			</Card>
			{progressMeterSessionId && (
				<ProgressMeter
					open={progressMeterOpen}
					sessionId={progressMeterSessionId}
					dialogTitle={`${translate("Submitting")}...`}
					initialCaption=""
					onComplete={async (_e: StorefrontProgress) => {
						return Promise.resolve().then(() => {
							props.checkoutFormState.setSubmitComplete(catalogid);
						});
					}}
					onFailed={async (_e: StorefrontProgress) => {
						return Promise.resolve().then(() => {
							//props.checkoutFormState.setProgressMeterSessionId(undefined);
						});
					}}
					onClose={async () => {
						return Promise.resolve().then(() => {
							props.checkoutFormState.setProgressMeterOpen(false);
							props.checkoutFormState.setProgressMeterSessionId(undefined);
						});
					}}
				/>
			)}
		</div>
	);
};
